import { PencilIcon, Trash2Icon } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import type { StockPolicyListRow } from "@/modules/inventory/domain/types";
import { receiveQtyHint } from "@/modules/inventory/presentation/lib/qty-unit-hint";
import { confirm } from "@/shared/lib/confirm";
import { formatQtyDisplay } from "@/shared/lib/format-number";
import { useDeactivateStockPolicyMutation } from "../../api/stock-policies-queries";
import {
  stockPolicyItemTypeLabel,
  stockPolicyScopeLabel,
} from "./stockPolicyLaoLabels";

type StockPoliciesTableProps = {
  rows: StockPolicyListRow[];
  canUpdate: boolean;
  onEdit: (row: StockPolicyListRow) => void;
};

export function StockPoliciesTable({
  rows,
  canUpdate,
  onEdit,
}: StockPoliciesTableProps) {
  const deactivate = useDeactivateStockPolicyMutation();

  const onDelete = async (row: StockPolicyListRow) => {
    const ok = await confirm({
      title: "ປິດໃຊ້ເກັນໄຂ?",
      description: `${row.itemName} · ${stockPolicyScopeLabel(row.branchId, row.branchCode)}`,
      actionText: "ປິດໃຊ້",
      ActionProps: { variant: "destructive" },
    });
    if (!ok) return;
    try {
      await deactivate.mutateAsync(row.id);
      toast.success("ປິດໃຊ້ເກັນໄຂແລ້ວ");
    } catch {
      toast.error("ປິດໃຊ້ລົ້ມເຫຼວ");
    }
  };

  if (rows.length === 0) {
    return (
      <p className="py-8 text-center text-muted-foreground text-sm">
        ບໍ່ມີເງື່ອນໄຂຕາມຕົວກອງ — ກົດ «ເພີ່ມລາຍການ» ເພື່ອຕັ້ງ min/target
      </p>
    );
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>ລາຍການ</TableHead>
          <TableHead>ປະເພດ</TableHead>
          <TableHead>ຂອບເຂດ</TableHead>
          <TableHead className="text-right">ຄົງເຫຼືອ</TableHead>
          <TableHead className="text-right">ຂັ້ນຕ່ຳ</TableHead>
          <TableHead className="text-right">ເປົ້າ</TableHead>
          <TableHead>ສະຖານະ</TableHead>
          {canUpdate ? <TableHead className="w-24" /> : null}
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((row) => (
          <TableRow key={row.id}>
            <TableCell>
              <span className="font-medium">{row.itemName}</span>
              <span className="ml-1 text-muted-foreground text-xs">
                ({row.unit})
              </span>
            </TableCell>
            <TableCell className="text-sm">
              {stockPolicyItemTypeLabel(row.itemType)}
            </TableCell>
            <TableCell className="text-sm">
              {stockPolicyScopeLabel(row.branchId, row.branchCode)}
            </TableCell>
            <TableCell className="text-right tabular-nums">
              {row.onHand != null ? (
                <>
                  {formatQtyDisplay(row.onHand)} {row.unit}
                  {receiveQtyHint(
                    row.onHand,
                    row.unit,
                    row.receiveUnit,
                    row.receiveToBase,
                  ) ? (
                    <div className="text-muted-foreground text-xs">
                      (
                      {receiveQtyHint(
                        row.onHand,
                        row.unit,
                        row.receiveUnit,
                        row.receiveToBase,
                      )}
                      )
                    </div>
                  ) : null}
                </>
              ) : (
                "—"
              )}
            </TableCell>
            <TableCell className="text-right tabular-nums">
              {formatQtyDisplay(row.minQty)}
            </TableCell>
            <TableCell className="text-right tabular-nums">
              {row.targetQty != null ? formatQtyDisplay(row.targetQty) : "—"}
            </TableCell>
            <TableCell>
              {row.isBelowMin ? (
                <Badge variant="destructive">ຕ່ຳກວ່າເກັນໄຂ</Badge>
              ) : row.onHand != null ? (
                <Badge variant="secondary">ພໍດີ</Badge>
              ) : null}
            </TableCell>
            {canUpdate ? (
              <TableCell>
                <div className="flex gap-1">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    aria-label="ແກ້ໄຂ"
                    onClick={() => onEdit(row)}
                  >
                    <PencilIcon className="size-4" />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    aria-label="ປິດໃຊ້"
                    onClick={() => void onDelete(row)}
                  >
                    <Trash2Icon className="size-4" />
                  </Button>
                </div>
              </TableCell>
            ) : null}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
